import React, { useCallback, useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import isBetweenPlugin from 'dayjs/plugin/isBetween';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import CustomStaticPeriodDatePicker from '../../../../../Common/CustomStaticPeriodDatePicker';
import { useDispatch, useSelector } from 'react-redux';
import * as yup from 'yup';
import styles from './ExtraWorkingTimeModal.module.scss';
import { Field, Form, Formik } from 'formik';
import { getWorker, getWorkers } from '../../../../store/selectors';
import { salonManagementActions } from '../../../../store';
import CustomTimePicker from '../../../../../Common/CustomTimePicker';
import WeekDaySelector from '../../../../../Common/WeekDaySelector';
import clx from 'classnames';
import SelectorMenu from '../../../../../Common/CustomSelector/SelectorMenu';
import { schedulerActions } from '../../../../../Scheduler/store';
import { getClosureApiFlags } from '../../../../../Scheduler/containers/utils/closureHelpers';
import useWindowSize from '../../../../../../utils/useWindowSize';
import TextField from '../../../../../Common/TextField';
import ConfirmModal from 'Containers/Common/ConfirmModal';
import { getActiveSalonePaymentInfo } from 'utils/salonToken';
import { Plus, Trash2 } from 'lucide-react';

dayjs.extend(isBetweenPlugin);

const normalizeWorkerId = (id) => {
  if (id == null || id === '') return null;
  const n = Number(id);
  return Number.isFinite(n) ? n : id;
};

const sameWorkerId = (a, b) => String(a) === String(b);

const findBlockedDates = (workers, activeWorkers = []) => {
  const filteredWorkers = workers.filter((worker) => activeWorkers.includes(worker.id));
  const closuresDates = filteredWorkers.reduce((acc, worker) => {
    const workerClosuresDates =
      worker.closures?.map(({ data_chiusura }) => dayjs(data_chiusura).format('YYYY-MM-DD')) || [];
    return [...acc, ...workerClosuresDates];
  }, []);

  const extraWorkingDates = filteredWorkers.reduce((acc, worker) => {
    const workerExtraWorkingDates =
      worker.extraWorkingTime?.reduce((accInner, { dataInizio, dataFine, giorno }) => {
        const dif = dataInizio && dataFine && dayjs(dataFine).diff(dataInizio, 'day');
        if (dif >= 0) {
          const days =
            Array.from({ length: dif + 1 }, (_, i) =>
              dayjs(dataInizio).add(i, 'days').format('YYYY-MM-DD'),
            ).filter((day) => dayjs(day).day() === +giorno) || [];
          return [...accInner, ...days];
        }
        return accInner;
      }, []) || [];
    return [...acc, ...workerExtraWorkingDates];
  }, []);

  return [...new Set([...closuresDates, ...extraWorkingDates])];
};

const matchesExtraOnDay = (extra, startDay) =>
  !!(
    extra?.dataInizio &&
    extra?.dataFine &&
    +extra.giorno === dayjs(startDay).day() &&
    dayjs(startDay).isBetween(extra.dataInizio, extra.dataFine, 'day', '[]')
  );

const findExtraOpeningOnDay = (worker, startDay) =>
  worker?.extraWorkingTime?.find((extra) => matchesExtraOnDay(extra, startDay));

/** Poltrone che hanno già un'apertura straordinaria quel giorno → vanno mostrate con − */
const findWorkersWithExtraOpening = (workers, startDay) => {
  if (!startDay) return [];
  return workers.reduce((acc, worker) => {
    const extra = findExtraOpeningOnDay(worker, startDay);
    if (!extra) return acc;
    const id = normalizeWorkerId(extra.idPoltrona ?? worker.id ?? worker.idpoltrona);
    return id != null ? [...acc, id] : acc;
  }, []);
};

const getExtraOpeningOrariId = (workers, workerId, startDay) => {
  if (!startDay || workerId == null) return null;
  const worker = workers.find(
    (w) => sameWorkerId(w.id, workerId) || sameWorkerId(w.idpoltrona, workerId),
  );
  return findExtraOpeningOnDay(worker, startDay)?.idPoltronaOrari || null;
};

const findBlockedWorkers = (workers, startDay) => {
  // Solo chiusure: le aperture esistenti si gestiscono con − (selezionate), non bloccate.
  return workers.reduce((acc, worker) => {
    const workerClosure = worker.closures?.find(({ data_chiusura }) =>
      dayjs(data_chiusura).isSame(startDay, 'day'),
    )?.poltrona;
    return workerClosure ? [...acc, workerClosure] : acc;
  }, []);
};

const Section = ({ title, hint, children, className }) => (
  <section className={clx(styles.section, className)}>
    <div className={styles.section_head}>
      <h3>{title}</h3>
      {hint ? <p>{hint}</p> : null}
    </div>
    {children}
  </section>
);

export default ({ closeModal, isOneDay = false, dayValue }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { isMobile } = useWindowSize();
  const [searchParams] = useSearchParams();
  const worker = useSelector(getWorker());
  const workers = useSelector(getWorkers());
  const bodyRef = useRef(null);
  const formedWorkers = workers.map((item) => ({
    name: item.name,
    value: normalizeWorkerId(item.id),
    icon: item.icon,
  }));
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [extraWorkingTime, setExtraWorkingTime] = useState({});
  const paymentDisabled = getActiveSalonePaymentInfo();

  useEffect(() => {
    dispatch(salonManagementActions.GET_WORKERS.REQUEST({ query: { workTime: true } }));
  }, [dispatch]);

  const refreshCalendarDay = useCallback(() => {
    if (!isOneDay || !dayValue?.startDay) return;
    const startDay =
      searchParams.get('startDay') || dayjs(dayValue.startDay).format('YYYY-MM-DD');
    const numberOfActiveDays = +(searchParams.get('numberOfActiveDays') || 1);
    dispatch(
      schedulerActions.GET_CALENDAR_DATA.REQUEST(
        {
          startDate: dayjs(startDay).format('YYYY-MM-DD'),
          endDate: dayjs(startDay).add(numberOfActiveDays, 'day').format('YYYY-MM-DD'),
          ...getClosureApiFlags(searchParams),
        },
        null,
        { skipLoader: true },
      ),
    );
  }, [dayValue?.startDay, dispatch, isOneDay, searchParams]);

  const handleExtraWorkingTimeSuccess = () => {
    if (isOneDay) {
      refreshCalendarDay();
      closeModal();
      return;
    }
    closeModal();
    window.location.reload();
  };

  const submitRequest = (req) => {
    if (isOneDay) {
      return dispatch(
        schedulerActions.SET_CALENDAR_WORKER_EXTRA_WORKING_TIME.REQUEST(
          req,
          handleExtraWorkingTimeSuccess,
        ),
      );
    }
    return dispatch(
      salonManagementActions.SET_WORKER_EXTRA_WORKING_TIME.REQUEST(req, handleExtraWorkingTimeSuccess),
    );
  };

  const initialActiveWorkers = [
    ...new Set(
      [
        normalizeWorkerId(worker?.idpoltrona),
        ...(isOneDay ? findWorkersWithExtraOpening(workers, dayValue.startDay) : []),
      ].filter((id) => id != null),
    ),
  ];

  const initialValues = {
    datePeriod: {
      start: isOneDay ? dayjs(dayValue.startDay) : null,
      end: isOneDay ? dayjs(dayValue.startDay) : null,
      blockedDates: isOneDay ? [] : findBlockedDates(workers, [worker?.idpoltrona]),
      daysIndex: isOneDay ? [dayjs(dayValue.startDay).day()] : [],
    },
    morningDateFrom: dayjs().hour(7).minute(0).second(0).millisecond(0),
    morningDateTo: dayjs().hour(0).minute(0).second(0).millisecond(0),
    eveningDateFrom: dayjs().hour(0).minute(0).second(0).millisecond(0),
    eveningDateTo: dayjs().hour(20).minute(0).second(0).millisecond(0),
    isFullDay: true,
    activeWorkersIds: initialActiveWorkers,
    blockedWorkers: isOneDay ? findBlockedWorkers(workers, dayValue.startDay) : [],
    note: '',
  };

  const validationSchema = {
    datePeriod: yup
      .object()
      .shape({
        start: yup
          .date()
          .typeError(t('workers.extraWorkingTime.validation.selectDate'))
          .required(t('workers.extraWorkingTime.validation.selectDate')),
        end: yup.date().typeError(t('workers.extraWorkingTime.validation.selectDate')).required(),
        daysIndex: yup
          .array()
          .typeError(t('workers.extraWorkingTime.validation.selectDay'))
          .min(1, t('workers.extraWorkingTime.validation.selectAtLeastOneDay'))
          .required(),
      })
      .test('checkDate', t('workers.extraWorkingTime.validation.dateAlreadyTaken'), (val) => {
        const { start, end, daysIndex = [], blockedDates } = val;
        const matchedDate = blockedDates.find(
          (closureDate) =>
            daysIndex.includes(dayjs(closureDate).day()) &&
            start &&
            end &&
            dayjs(closureDate).isBetween(start, end, null, '[]'),
        );
        return !matchedDate;
      }),
    morningDateFrom: yup
      .date()
      .typeError(t('workers.extraWorkingTime.validation.selectTime'))
      .required(t('workers.extraWorkingTime.validation.enterTime')),
    morningDateTo: yup
      .date()
      .typeError(t('workers.extraWorkingTime.validation.selectTime'))
      .when('isFullDay', ([isFullDay], schema) =>
        isFullDay ? schema.nullable(true) : schema.required(t('workers.extraWorkingTime.validation.enterTime')),
      ),
    eveningDateFrom: yup
      .date()
      .typeError(t('workers.extraWorkingTime.validation.selectTime'))
      .when('isFullDay', ([isFullDay], schema) =>
        isFullDay ? schema.nullable(true) : schema.required(t('workers.extraWorkingTime.validation.enterTime')),
      ),
    eveningDateTo: yup
      .date()
      .typeError(t('workers.extraWorkingTime.validation.selectTime'))
      .required(t('workers.extraWorkingTime.validation.enterTime')),
    activeWorkersIds: yup
      .array()
      .typeError(t('workers.extraWorkingTime.validation.selectWorker'))
      .min(1, t('workers.extraWorkingTime.validation.selectWorker'))
      .required(),
  };

  return (
    <>
      <ConfirmModal
        isModalOpen={openConfirmModal}
        setIsModalOpen={() => submitRequest({ ...extraWorkingTime, mustPay: false })}
        title={t('workers.extraWorkingTime.modal.confirmPayment.title')}
        action={() => submitRequest({ ...extraWorkingTime, mustPay: true })}
        content={
          <div className={styles.confirm_body} style={{ marginBottom: isMobile ? '60px' : '0px' }}>
            <p>{t('workers.extraWorkingTime.modal.confirmPayment.content1')}</p>
            <p>{t('workers.extraWorkingTime.modal.confirmPayment.content2')}</p>
            <p>{t('workers.extraWorkingTime.modal.confirmPayment.content3')}</p>
          </div>
        }
      />

      <div
        className={clx(styles.page, {
          [styles.page_one_day]: isOneDay,
          [styles.page_mobile]: isMobile,
        })}
      >
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={yup.object().shape(validationSchema)}
          onSubmit={(values) => {
            const req = {
              activeWorkersIds: values.activeWorkersIds,
              idPoltrona: worker.idpoltrona,
              dataInizio: values.datePeriod?.start.format('YYYY-MM-DD'),
              dataFine: values.datePeriod?.end.format('YYYY-MM-DD'),
              oraInizioMattina: values.morningDateFrom.format('HH:mm:ss'),
              oraFineMattina: values.isFullDay ? '00:00:00' : values.morningDateTo.format('HH:mm:ss'),
              oraInizioPomeriggio: values.isFullDay
                ? '00:00:00'
                : values.eveningDateFrom.format('HH:mm:ss'),
              oraFinePomeriggio: values.eveningDateTo.format('HH:mm:ss'),
              daysIndex: values.datePeriod?.daysIndex,
              note: values.note,
            };
            if (paymentDisabled == 1) {
              return submitRequest(req);
            }
            setExtraWorkingTime(req);
            setOpenConfirmModal(true);
          }}
        >
          {({ values, setFieldValue, errors, touched }) => (
              <Form className={styles.form}>
                <div className={styles.body} ref={bodyRef}>
                  {!isOneDay ? (
                    <Section title={t('workers.extraWorkingTime.modal.chooseDays')}>
                      <WeekDaySelector
                        dayIndex={values.datePeriod?.daysIndex}
                        setDayIndex={(index) => setFieldValue('datePeriod.daysIndex', index)}
                        isMultiply
                        error={errors?.datePeriod?.daysIndex}
                      />
                    </Section>
                  ) : null}

                  <div className={styles.split_row}>
                    {!isOneDay ? (
                      <Section
                        title={t('workers.extraWorkingTime.modal.selectPeriod')}
                        className={styles.period_section}
                      >
                        <CustomStaticPeriodDatePicker
                          period={values.datePeriod}
                          setPeriod={(period) =>
                            setFieldValue('datePeriod', { ...values.datePeriod, ...period })
                          }
                          error={errors.datePeriod?.start || errors.datePeriod?.end}
                          touched={touched.datePeriod}
                          disabledDates={values.datePeriod.blockedDates}
                        />
                      </Section>
                    ) : null}

                    <Section
                      title={t('workers.extraWorkingTime.modal.setSchedule')}
                      hint={
                        values.isFullDay
                          ? t('workers.extraWorkingTime.modal.scheduleHintContinuous')
                          : t('workers.extraWorkingTime.modal.scheduleHintSplit')
                      }
                      className={styles.schedule_section}
                    >
                      {values.isFullDay ? (
                        <div className={styles.time_row}>
                          <CustomTimePicker
                            title={t('workers.extraWorkingTime.modal.from')}
                            value={values.morningDateFrom}
                            setValue={(time) => setFieldValue('morningDateFrom', time)}
                            maxTime={values.eveningDateTo}
                            error={touched.morningDateFrom && errors.morningDateFrom}
                          />
                          <CustomTimePicker
                            title={t('workers.extraWorkingTime.modal.to')}
                            value={values.eveningDateTo}
                            setValue={(time) => setFieldValue('eveningDateTo', time)}
                            minTime={values.morningDateFrom}
                            error={touched.eveningDateTo && errors.eveningDateTo}
                          />
                        </div>
                      ) : (
                        <div className={styles.split_blocks}>
                          <div className={styles.slot_block}>
                            <div className={styles.slot_head}>
                              <span>{t('workers.extraWorkingTime.modal.morningSlot')}</span>
                              <button
                                type="button"
                                className={styles.icon_btn}
                                onClick={() => setFieldValue('isFullDay', true)}
                                aria-label={t('workers.extraWorkingTime.modal.removeSplit')}
                              >
                                <Trash2 size={15} strokeWidth={2.3} />
                              </button>
                            </div>
                            <div className={styles.time_row}>
                              <CustomTimePicker
                                title={t('workers.extraWorkingTime.modal.from')}
                                value={values.morningDateFrom}
                                setValue={(time) => setFieldValue('morningDateFrom', time)}
                                maxTime={values.morningDateTo}
                                error={touched.morningDateFrom && errors.morningDateFrom}
                              />
                              <CustomTimePicker
                                title={t('workers.extraWorkingTime.modal.to')}
                                value={values.morningDateTo}
                                setValue={(time) => setFieldValue('morningDateTo', time)}
                                minTime={values.morningDateFrom}
                                error={touched.morningDateTo && errors.morningDateTo}
                              />
                            </div>
                          </div>

                          <div className={styles.slot_block}>
                            <div className={styles.slot_head}>
                              <span>{t('workers.extraWorkingTime.modal.afternoonSlot')}</span>
                              <button
                                type="button"
                                className={styles.icon_btn}
                                onClick={() => setFieldValue('isFullDay', true)}
                                aria-label={t('workers.extraWorkingTime.modal.removeSplit')}
                              >
                                <Trash2 size={15} strokeWidth={2.3} />
                              </button>
                            </div>
                            <div className={styles.time_row}>
                              <CustomTimePicker
                                title={t('workers.extraWorkingTime.modal.from')}
                                value={values.eveningDateFrom}
                                setValue={(time) => setFieldValue('eveningDateFrom', time)}
                                maxTime={values.eveningDateTo}
                                error={touched.eveningDateFrom && errors.eveningDateFrom}
                              />
                              <CustomTimePicker
                                title={t('workers.extraWorkingTime.modal.to')}
                                value={values.eveningDateTo}
                                setValue={(time) => setFieldValue('eveningDateTo', time)}
                                minTime={values.eveningDateFrom}
                                error={touched.eveningDateTo && errors.eveningDateTo}
                              />
                            </div>
                          </div>
                        </div>
                      )}

                      {values.isFullDay ? (
                        <button
                          type="button"
                          className={styles.link_btn}
                          onClick={() => {
                            setFieldValue('isFullDay', false);
                            setFieldValue(
                              'morningDateTo',
                              dayjs().hour(13).minute(0).second(0).millisecond(0),
                            );
                            setFieldValue(
                              'eveningDateFrom',
                              dayjs().hour(14).minute(0).second(0).millisecond(0),
                            );
                          }}
                        >
                          <Plus size={15} strokeWidth={2.4} aria-hidden />
                          {t('workers.extraWorkingTime.modal.addSchedule')}
                        </button>
                      ) : null}
                    </Section>
                  </div>

                  <Section title={t('workers.extraWorkingTime.modal.chairs')}>
                    <SelectorMenu
                      isMultiple
                      fullHeight
                      setIsDropdownOpen={() => {}}
                      options={formedWorkers}
                      setValue={(rawNextIds) => {
                        const nextIds = (rawNextIds || [])
                          .map(normalizeWorkerId)
                          .filter((id) => id != null);
                        const prevIds = values.activeWorkersIds || [];
                        const removedIds = prevIds.filter(
                          (id) => !nextIds.some((n) => sameWorkerId(n, id)),
                        );
                        const dayRef = isOneDay
                          ? dayValue?.startDay
                          : values.datePeriod?.start;

                        let pendingDeletes = 0;
                        removedIds.forEach((workerId) => {
                          const orariId = getExtraOpeningOrariId(workers, workerId, dayRef);
                          if (!orariId) return;
                          pendingDeletes += 1;
                          dispatch(
                            salonManagementActions.DELETE_WORKER_EXTRA_TIME.REQUEST(
                              { idPoltronaOrari: orariId },
                              () => {
                                pendingDeletes -= 1;
                                if (pendingDeletes > 0) return;
                                refreshCalendarDay();
                                // Rimozione fatta: se non restano poltrone chiudi (Salva richiederebbe min 1)
                                if (nextIds.length === 0 && isOneDay) {
                                  closeModal();
                                }
                              },
                            ),
                          );
                        });

                        setFieldValue('activeWorkersIds', nextIds);
                        setFieldValue(
                          'datePeriod.blockedDates',
                          findBlockedDates(workers, nextIds),
                        );
                      }}
                      value={values.activeWorkersIds}
                      error={touched.activeWorkersIds && errors.activeWorkersIds}
                      blockedValues={values.blockedWorkers}
                    />
                  </Section>

                  <Section
                    title={
                      <>
                        {t('workers.extraWorkingTime.modal.comment')}
                        <span className={styles.optional}>
                          {' '}
                          {t('workers.extraWorkingTime.modal.commentOptional')}
                        </span>
                      </>
                    }
                  >
                    <Field
                      name="note"
                      id="note"
                      textarea
                      placeholder={t('workers.extraWorkingTime.modal.commentPlaceholder')}
                      height="88px"
                      className={styles.input}
                      component={TextField}
                    />
                  </Section>
                </div>

                <div className={styles.footer}>
                  <button type="submit" className={styles.confirm_button}>
                    {t('common.actions.save')}
                  </button>
                </div>
              </Form>
            )}
        </Formik>
      </div>
    </>
  );
};
